<script setup>
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { hasConfigurableRegistrationEnabled, hasExperienceDetailsPageEnabled } from "@/composables/authorization.js";
import { ORDER_STATUS } from "@/constants";
import { endOfDayDate } from "@/util/date";
import MyExperiencesSummaryPanel from "./MyExperiencesSummaryPanel.vue";

const props = defineProps({
  experiences: {
    type: Array,
    default: [],
  },
});

const emit = defineEmits(["update-mobile-banner", "start-loading"]);

const route = useRoute();

const today = new Date();

const myUpcomingExperiencesFilter = (myApplications, cutoffDate) => {
  return myApplications.filter(
    (application) =>
      [ORDER_STATUS.submitted, ORDER_STATUS.paidConfirmed, ORDER_STATUS.fulfilled].includes(
        application.order_status,
      ) && endOfDayDate(application.end_date) >= cutoffDate,
  );
};

const myDraftExperiencesFilter = (myApplications, cutoffDate) => {
  return myApplications.filter(
    (application) =>
      [ORDER_STATUS.draft].includes(application.order_status) &&
      endOfDayDate(application.application_deadline) > cutoffDate,
  );
};

const mySavedInternshipsFilter = () => {
  return [];
};

const mySavedClassesFilter = () => {
  return [];
};

const myPastExperiencesFilter = (myApplications, cutoffDate) => {
  const pastWithdrawn = myApplications.filter((application) =>
    [ORDER_STATUS.withdrawn].includes(application.order_status),
  );
  const pastDraft = myApplications.filter(
    (application) =>
      [ORDER_STATUS.draft].includes(application.order_status) &&
      endOfDayDate(application.application_deadline) <= cutoffDate,
  );
  const pastCompleted = myApplications.filter(
    (application) =>
      [ORDER_STATUS.submitted, ORDER_STATUS.paidConfirmed, ORDER_STATUS.fulfilled].includes(
        application.order_status,
      ) && endOfDayDate(application.end_date) < cutoffDate,
  );
  return [...pastWithdrawn, ...pastDraft, ...pastCompleted];
};

const panelDefinitions = [
  {
    index: 0,
    key: "upcoming-experiences",
    title: "My Experiences",
    filterFn: myUpcomingExperiencesFilter,
    enabled: true,
    historical: false,
    buttonText: "Continue Enrollment",
    buttonRouteName: hasExperienceDetailsPageEnabled.value ? "experience-detail" : "applications/landing",
  },
  {
    index: 1,
    key: "draft-experiences",
    title: "Pick Up Where You Left Off",
    filterFn: myDraftExperiencesFilter,
    enabled: true,
    historical: false,
    buttonText: "Incomplete",
    buttonRouteName: hasConfigurableRegistrationEnabled.value ? "modify-application" : "legacy-application",
  },
  {
    index: 2,
    key: "saved-classes",
    title: "Saved Classes",
    filterFn: mySavedClassesFilter,
    enabled: false,
    historical: false,
  },
  {
    index: 3,
    key: "saved-internships",
    title: "Saved Internships",
    filterFn: mySavedInternshipsFilter,
    enabled: false,
    historical: false,
  },
  {
    index: 4,
    key: "past-experiences",
    title: "Past Experiences",
    filterFn: myPastExperiencesFilter,
    enabled: true,
    historical: true,
    buttonText: "Completed",
    buttonRouteName: hasExperienceDetailsPageEnabled.value ? "experience-detail" : "applications/landing",
  },
];

const activeDefinitions = computed(() =>
  panelDefinitions.filter(
    (definition) => definition.enabled && definition.filterFn(props.experiences, today).length > 0,
  ),
);

const filteredApplications = (myExperiences, definition) => {
  if (!definition) {
    return [];
  }
  return definition.filterFn(myExperiences, today);
};

const filteredApplicationCount = (myExperiences, definition) => {
  return filteredApplications(myExperiences, definition).length ?? 0;
};

const tabHeaderLabel = (myExperiences, definition) => {
  return [definition.title, " (", filteredApplicationCount(myExperiences, definition), ")"].join("");
};

const initialOpenTabs = computed(() => {
  const defaultTab = (activeDefinitions.value?.map((_, index) => index) ?? [0])[0];
  const openTabParams = (route.query["open"] ?? `${defaultTab}`)
    .split(",")
    .filter((tabIndex) => tabIndex && !isNaN(tabIndex))
    .map((tabIndex) => Number.parseInt(tabIndex));
  return openTabParams.length > 0 ? openTabParams : [0];
});

const ptStylingAccordion = {
  root: {
    class: "w-full flex-nowrap",
  },
};

const ptStylingAccordionTab = (index) => {
  return {
    root: {
      class: "!m-0",
    },
    toggleableContent: {
      class: "",
    },
    content: {
      class: "bg-white border-0 py-4 px-6",
    },
    header: {
      class: `p-4 bg-white ${index > 0 ? "border-0 border-t border-gray-skeleton" : "rounded-xl"}`,
    },
    headerTitle: {
      class: "font-montserrat font-bold normal-case text-base text-[#334155]",
    },
    headerIcon: {
      class: "absolute right-0",
    },
  };
};

onMounted(() => {
  let sessionNames = [];
  let pastOnly = false;
  if (activeDefinitions.value.length === 1 && activeDefinitions.value[0].historical) {
    pastOnly = true;
  }
  if (activeDefinitions.value.length > 0 && activeDefinitions.value.some((definition) => definition.index === 0)) {
    const activeExperiences = activeDefinitions.value
      .filter((definition) => definition.index === 0)[0]
      .filterFn(props.experiences, today);
    sessionNames = activeExperiences.length > 0 ? activeExperiences.map((experience) => experience.session_name) : [];
  }
  emit("update-mobile-banner", sessionNames, !pastOnly);
});
</script>
<template>
  <div class="flex w-full pb-20">
    <Accordion
      :active-index="initialOpenTabs"
      :multiple="true"
      :pt="ptStylingAccordion"
      data-testid="my-experiences-overview-accordion"
    >
      <AccordionTab
        v-for="(panelDefinition, index) in activeDefinitions"
        :key="panelDefinition.key"
        :value="panelDefinition.index"
        :header="tabHeaderLabel(experiences, panelDefinition)"
        :pt="ptStylingAccordionTab(index)"
        :data-testid="`my-experiences-overview-tab-${index}`"
      >
        <MyExperiencesSummaryPanel
          :key="panelDefinition.key"
          :panel-definition="panelDefinition"
          :experience-list="filteredApplications(experiences, panelDefinition)"
          :cutoff-date="today"
          @start-loading="emit('start-loading')"
        />
      </AccordionTab>
    </Accordion>
  </div>
</template>
